import { useUserSubordinateQuery } from "@/common/api/user/use-user-subordinate.query";
import { useUserSupervisorQuery } from "@/common/api/user/use-user-supervisor.query";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import { Skeleton } from "@/components/ui/skeleton";
import { UserItem } from "@/components/user-item/user-item";
import { ArrowRight, UserRoundCog, Users } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

const MAX_SUPERVISORS = 100;
const MAX_SUBORDINATES = 100;

const SUPERVISORS_QUERY_PARAMS = { parameters: { pageSize: MAX_SUPERVISORS } };
const SUBORDINATES_QUERY_PARAMS = { parameters: { pageSize: MAX_SUBORDINATES } };

export function HierarchyTab({ userUuid }: { userUuid: string }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const { data: supervisors, isPending: supervisorsPending } = useUserSupervisorQuery(
    userUuid,
    SUPERVISORS_QUERY_PARAMS,
  );
  const { data: subordinates, isPending: subordinatesPending } = useUserSubordinateQuery(
    userUuid,
    SUBORDINATES_QUERY_PARAMS,
  );

  const supervisorList = supervisors?.data ?? [];
  const subordinateList = subordinates?.data ?? [];

  return (
    <DetailTabGrid className="xl:grid-cols-2">
      <DetailTabCard
        icon={UserRoundCog}
        title={withCount(t("masterdata:Supervisors"), supervisorList.length, supervisorsPending)}
      >
        {supervisorsPending ? (
          <Skeleton className="h-8 w-full" />
        ) : supervisorList.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 py-1.5">
            {supervisorList.map((supervisor, index) => (
              <div key={supervisor.uuid} className="flex items-center gap-2">
                <UserItem user={supervisor} to={`/${supervisor.uuid}`} />
                {index < supervisorList.length - 1 && (
                  <ArrowRight className="text-primary dark:text-primary/80 h-4 w-4" />
                )}
              </div>
            ))}
            {supervisors && supervisors.meta.total > MAX_SUPERVISORS && (
              <MoreBadge count={supervisors.meta.total - MAX_SUPERVISORS} />
            )}
          </div>
        ) : (
          <div className="py-1.5">
            <EmptyValue />
          </div>
        )}
      </DetailTabCard>

      <DetailTabCard
        icon={Users}
        title={withCount(t("masterdata:TeamMembers"), subordinateList.length, subordinatesPending)}
      >
        {subordinatesPending ? (
          <Skeleton className="h-8 w-full" />
        ) : subordinateList.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 py-1.5">
            {subordinateList.map((subordinate) => (
              <UserItem
                key={subordinate.uuid}
                user={subordinate}
                to={`/${subordinate.uuid}`}
                highlighted={subordinate.depth === 1}
                tooltip={
                  subordinate.depth === 1
                    ? t("masterdata:DirectEmployee")
                    : t("masterdata:EmployeeAtLevel", { depth: subordinate.depth })
                }
                badge={
                  subordinate.depth === 1 ? (
                    <Badge
                      variant={BadgeVariant.Outlined}
                      colorVariant={BadgeColorVariant.Secondary}
                      className="bg-primary/80 text-primary-foreground border-primary/30 dark:bg-primary/20 dark:text-primary dark:border-primary/50 text-xs"
                    >
                      {t("masterdata:Direct")}
                    </Badge>
                  ) : undefined
                }
              />
            ))}
            {subordinates && subordinates.meta.total > MAX_SUBORDINATES && (
              <MoreBadge count={subordinates.meta.total - MAX_SUBORDINATES} />
            )}
          </div>
        ) : (
          <div className="py-1.5">
            <EmptyValue />
          </div>
        )}
      </DetailTabCard>
    </DetailTabGrid>
  );
}

function withCount(label: string, count: number, isPending: boolean): string {
  return isPending ? label : `${label} (${count})`;
}

function MoreBadge({ count }: { count: number }): ReactElement {
  const { t } = useTranslation("common");

  return (
    <Badge variant={BadgeVariant.Filled} colorVariant={BadgeColorVariant.Secondary} className="text-xs">
      +{count} {t("common:More")}
    </Badge>
  );
}
